import React, { useEffect, useState } from 'react';
import { List, Avatar, Badge, Typography, Row, Col, Tooltip } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatTime, getStatusColor } from '../conversationUtils';
import { useTheme } from '../../../Shared/GlobalContext/ThemeContext';
import CountdownPill, { formatDuration } from '../../../Shared/CountdownPill';
import TypingIndicator from '../../../Shared/TypingIndicator';
import { detectTextDirection } from '../../../../Utils/textDirection';

const { Text } = Typography;

const ConversationListItem = React.memo(({
    conversation,
    selectedId,
    onSelectConversation,
    index,
    hoveredIndex,
    setHoveredIndex
}) => {
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const { contact, last_message, status, updated_time, id, is_writing } = conversation;

    const appliedAt = conversation?.processing_delay_applied_at;
    const appliedSeconds = conversation?.processing_delay_applied_seconds;

    const [secondsUntilProcessing, setSecondsUntilProcessing] = useState(0);

    // Calculate countdown based on time, not is_writing flag.
    // The backend may merge countdown and is_writing events together,
    // so we calculate countdown based on time and let it expire naturally.
    useEffect(() => {
        if (!appliedAt || !appliedSeconds) {
            setSecondsUntilProcessing(0);
            return;
        }

        const startTime = new Date(appliedAt).getTime();
        const durationMs = appliedSeconds * 1000;
        const endTime = startTime + durationMs;

        const updateCountdown = () => {
            const now = window.socketService ? window.socketService.getCorrectedNow() : Date.now();
            const rawRemaining = (endTime - now) / 1000;
            const remaining = Math.max(0, Math.ceil(rawRemaining));

            // Debugging the countdown issue
            console.log(
                `[Countdown Debug] Conversation: ${conversation?.id}\n` +
                `- appliedAt: ${new Date(appliedAt).toISOString()}\n` +
                `- appliedSeconds: ${appliedSeconds}\n` +
                `- calculated endTime: ${new Date(endTime).toISOString()}\n` +
                `- current time (now): ${new Date(now).toISOString()}\n` +
                `- raw remaining (seconds): ${rawRemaining}\n` +
                `- final clamped remaining for UI: ${remaining}`
            );

            setSecondsUntilProcessing(remaining);
            return remaining;
        };

        // Initial update
        const initialRemaining = updateCountdown();
        if (initialRemaining <= 0) return;

        const intervalId = setInterval(() => {
            const remaining = updateCountdown();
            if (remaining <= 0) {
                clearInterval(intervalId);
            }
        }, 1000);

        return () => {
            clearInterval(intervalId);
        };
    }, [appliedAt, appliedSeconds, conversation?.id]);

    const themeColors = {
        background: isDarkMode ? '#1f1f1f' : '#fff',
        selectedBackground: isDarkMode ? '#183a61' : '#e6f7ff',
        hoverBackground: isDarkMode ? '#2a2a2a' : '#f5f5f5',
        text: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        secondaryText: isDarkMode ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
        typingColor: '#1890ff',
    };

    // Determine preview text for the conversation item
    const hasContent = Boolean(last_message?.content);
    const hasMedias = Array.isArray(last_message?.medias) && last_message.medias.length > 0;
    const previewText = !last_message
        ? t('no_messages_yet')
        : (!hasContent && !hasMedias && last_message.sender_type === 'agent')
            ? t('agent_declined_to_respond')
            : (!hasContent && hasMedias)
                ? t('media_message')
                : (last_message.content || t('no_messages_yet'));


    const isUnread = conversation?.is_seen_by_user === false;
    const timeColor = isUnread
        ? (isDarkMode ? 'rgba(255, 255, 255, 0.65)' : '#4b4f56') // darker for unread
        : (isDarkMode ? 'rgba(255, 255, 255, 0.35)' : '#8a8d91'); // lighter for read

    return (
        <List.Item
            style={{
                cursor: 'pointer',
                background: selectedId === id ? themeColors.selectedBackground : themeColors.background,
                padding: '8px 16px',
                position: 'relative',
                transition: 'background-color 0.3s ease',
                ':hover': {
                    background: themeColors.hoverBackground
                }
            }}
            onClick={() => onSelectConversation(conversation)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
        >
            <List.Item.Meta
                avatar={
                    <Badge status={getStatusColor(status)}>
                        <Avatar
                            src={contact?.picture_url}
                            icon={!contact?.picture_url && <UserOutlined />}
                        />
                    </Badge>
                }
                title={
                    <Row justify="space-between" align="middle" wrap={false} style={{ width: '100%', flexWrap: 'nowrap' }}>
                        <Col flex="auto" style={{ overflow: 'hidden', marginRight: 8, minWidth: 0 }}>
                            <Text style={{ color: themeColors.text, fontWeight: isUnread ? 600 : 400 }} ellipsis>
                                {`${contact?.first_name || ''} ${contact?.last_name || ''}`.trim() || t('unknown_contact')}
                            </Text>
                        </Col>
                        <Col flex="none">
                            <Tooltip title={new Date(updated_time).toLocaleString()}>
                                <Text type="secondary" style={{ fontSize: 12, color: timeColor, whiteSpace: 'nowrap' }}>
                                    {formatTime(updated_time)}
                                </Text>
                            </Tooltip>
                        </Col>
                    </Row>
                }
                description={
                    <div>
                        {/* Countdown takes priority over typing if still active */}
                        {secondsUntilProcessing > 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                <Text
                                    ellipsis
                                    dir={detectTextDirection(previewText)}
                                    style={{
                                        color: isUnread ? themeColors.text : themeColors.secondaryText,
                                        fontWeight: isUnread ? 600 : 400,
                                        flex: '0 1 auto',
                                        minWidth: 0,
                                        unicodeBidi: 'plaintext'
                                    }}
                                >
                                    {previewText}
                                </Text>
                                <Tooltip title={`Processing starts in ${formatDuration(secondsUntilProcessing)}`}>
                                    <div style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6 }}>
                                        <CountdownPill seconds={secondsUntilProcessing} isDarkMode={isDarkMode} size="sm" />
                                    </div>
                                </Tooltip>
                            </div>
                        ) : is_writing ? (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <TypingIndicator />
                                <Text type="secondary" style={{ fontSize: 14, color: themeColors.typingColor }}>
                                    {t('agent_is_typing')}
                                </Text>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                <Text
                                    ellipsis
                                    dir={detectTextDirection(previewText)}
                                    style={{
                                        color: isUnread ? themeColors.text : themeColors.secondaryText,
                                        fontWeight: isUnread ? 600 : 400,
                                        flex: '0 1 auto',
                                        minWidth: 0,
                                        unicodeBidi: 'plaintext'
                                    }}
                                >
                                    {previewText}
                                </Text>
                            </div>
                        )}
                    </div>
                }
            />
        </List.Item>
    );
});

// Optimize: Custom comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
    // Only re-render if these specific fields change
    return (
        prevProps.conversation?.id === nextProps.conversation?.id &&
        prevProps.conversation?.is_writing === nextProps.conversation?.is_writing &&
        prevProps.conversation?.processing_delay_applied_at === nextProps.conversation?.processing_delay_applied_at &&
        prevProps.conversation?.processing_delay_applied_seconds === nextProps.conversation?.processing_delay_applied_seconds &&
        prevProps.conversation?.is_seen_by_user === nextProps.conversation?.is_seen_by_user &&
        prevProps.conversation?.updated_time === nextProps.conversation?.updated_time &&
        prevProps.conversation?.last_message?.content === nextProps.conversation?.last_message?.content &&
        prevProps.selectedId === nextProps.selectedId &&
        prevProps.index === nextProps.index &&
        prevProps.hoveredIndex === nextProps.hoveredIndex
    );
};

export default React.memo(ConversationListItem, arePropsEqual);
